import {
  CalendarDaysIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'assets/icons';
import Button from 'components/button';
import { addDays, isoDay, monthName } from 'helper/timeLabels';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import { isoWeek, mondayOf, startOfDay } from './statsPeriods';

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);
// « Janv. », « Févr. »…
const shortMonth = (month) =>
  capitalize(
    new Date(2026, month, 1).toLocaleDateString('fr-FR', { month: 'short' }),
  );
// The six weeks (Mondays) shown for a month.
const gridOf = (year, month) =>
  Array.from({ length: 6 }, (_, index) =>
    addDays(mondayOf(new Date(year, month, 1)), 7 * index),
  );

/**
 * The choice of the period, opened from its name: a week in a calendar, a
 * month, a quarter, or a period picked freely (two clicks: the first day,
 * then the last). `onPick(kind, day)` and `onRange(from, to)` apply it.
 */
const PeriodPicker = ({ period, label, onPick, onRange }) => {
  const { text, plural } = useText('stats');
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const today = startOfDay(new Date());
  const [cursor, setCursor] = useState(() => ({
    year: period.start.getFullYear(),
    month: period.start.getMonth(),
  }));
  const [range, setRange] = useState({ from: null, to: null });
  const byMonth = period.kind === 'week' || period.kind === 'custom';

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!box.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const toggle = () => {
    if (!open) {
      const anchor =
        period.kind === 'week'
          ? addDays(period.start, 3)
          : period.kind === 'custom'
            ? period.start
            : new Date(period.year, period.first, 1);
      setCursor({ year: anchor.getFullYear(), month: anchor.getMonth() });
      setRange(
        period.kind === 'custom'
          ? { from: period.start, to: addDays(period.end, -1) }
          : { from: null, to: null },
      );
    }
    setOpen(!open);
  };
  const pick = (kind, day) => {
    setOpen(false);
    onPick(kind, day);
  };
  const step = (delta) =>
    setCursor(({ year, month }) => {
      if (!byMonth) return { year: year + delta, month };
      const next = new Date(year, month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  const title = byMonth
    ? `${capitalize(monthName(cursor.month))} ${cursor.year}`
    : String(cursor.year);
  const nextOff = byMonth
    ? new Date(cursor.year, cursor.month + 1, 1) > today
    : cursor.year >= today.getFullYear();

  const tile = (key, name, selected, disabled, onClick) => (
    <button
      key={key}
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`h-10 rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:text-gray-400 ${selected ? 'bg-yellow-500 text-white' : 'text-gray-900 enabled:hover:bg-gray-100'}`}
    >
      {name}
    </button>
  );

  let body;
  if (period.kind === 'month') {
    body = (
      <div className="grid grid-cols-3 gap-1">
        {Array.from({ length: 12 }, (_, month) =>
          tile(
            month,
            shortMonth(month),
            period.year === cursor.year && period.first === month,
            new Date(cursor.year, month, 1) > today,
            () => pick('month', new Date(cursor.year, month, 15)),
          ),
        )}
      </div>
    );
  } else if (period.kind === 'quarter') {
    body = (
      <div className="grid grid-cols-2 gap-1">
        {[0, 1, 2, 3].map((quarter) =>
          tile(
            quarter,
            text('quarterTile', {
              n: quarter + 1,
              from: shortMonth(quarter * 3).toLowerCase(),
              to: shortMonth(quarter * 3 + 2).toLowerCase(),
            }),
            period.year === cursor.year && period.first === quarter * 3,
            new Date(cursor.year, quarter * 3, 1) > today,
            () => pick('quarter', new Date(cursor.year, quarter * 3, 15)),
          ),
        )}
      </div>
    );
  } else {
    // A week: a row per week, clickable as a whole. A free period: one day
    // after the other.
    const week = period.kind === 'week';
    const { from, to } = range;
    const clickDay = (day) => {
      if (!from || to || day < from) setRange({ from: day, to: null });
      else setRange({ from, to: day });
    };
    const columns = week ? 'grid-cols-[36px_repeat(7,1fr)]' : 'grid-cols-7';
    body = (
      <div className="flex flex-col gap-0.5">
        <div
          className={`grid ${columns} text-center text-xs text-gray-400`}
          aria-hidden="true"
        >
          {week && <span />}
          {text('weekdayInitials')
            .split(',')
            .map((initial, index) => (
              <span key={index} className="py-1">
                {initial}
              </span>
            ))}
        </div>
        {gridOf(cursor.year, cursor.month).map((monday) => {
          const days = Array.from({ length: 7 }, (_, index) =>
            addDays(monday, index),
          );
          const cell = (day) => {
            const outside = day.getMonth() !== cursor.month;
            const isToday = isoDay(day) === isoDay(today);
            if (week)
              return (
                <span
                  key={isoDay(day)}
                  className={`flex h-8 items-center justify-center text-sm ${outside ? 'opacity-50' : ''} ${isToday ? 'font-semibold' : ''}`}
                >
                  {day.getDate()}
                </span>
              );
            const inRange = from && day >= from && day <= (to ?? from);
            const edge =
              from &&
              (isoDay(day) === isoDay(from) ||
                (to && isoDay(day) === isoDay(to)));
            let look = `rounded-md enabled:hover:bg-gray-100 ${outside ? 'text-gray-400' : 'text-gray-900'}`;
            if (inRange) look = 'bg-yellow-50 text-gray-900';
            if (edge) look = 'rounded-md bg-yellow-500 font-medium text-white';
            return (
              <button
                key={isoDay(day)}
                type="button"
                disabled={day > today}
                onClick={() => clickDay(day)}
                className={`flex h-8 items-center justify-center text-sm transition-colors disabled:cursor-not-allowed disabled:text-gray-300 ${look} ${isToday && !edge ? 'font-semibold' : ''}`}
              >
                {day.getDate()}
              </button>
            );
          };
          if (!week)
            return (
              <div key={isoDay(monday)} className="grid grid-cols-7">
                {days.map(cell)}
              </div>
            );
          const selected = isoDay(monday) === isoDay(period.start);
          return (
            <button
              key={isoDay(monday)}
              type="button"
              disabled={monday > today}
              onClick={() => pick('week', monday)}
              className={`grid grid-cols-[36px_repeat(7,1fr)] items-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${selected ? 'bg-yellow-500 text-white' : 'text-gray-900 enabled:hover:bg-gray-100'}`}
            >
              <span className={`text-xs ${selected ? '' : 'text-gray-400'}`}>
                {text('weekShort', { n: isoWeek(monday) })}
              </span>
              {days.map(cell)}
            </button>
          );
        })}
        {!week && (
          <div className="mt-2 flex items-center gap-2 border-t border-gray-100 pt-3">
            <p className="flex-1 text-xs text-gray-600">
              {!from && text('rangeFirst')}
              {from && !to && text('rangeLast')}
              {from &&
                to &&
                plural('rangeDays', Math.round((to - from) / 86400000) + 1)}
            </p>
            <Button
              color="PRIMARY"
              size="SM"
              disabled={!from}
              onClick={() => {
                setOpen(false);
                onRange(from, to ?? from);
              }}
            >
              {text('rangeApply')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  const arrow =
    'flex size-8 items-center justify-center rounded-md transition-colors enabled:hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={toggle}
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-base font-semibold text-gray-900 transition-colors hover:bg-gray-100"
      >
        <CalendarDaysIcon wh={18} color="#4B5563" />
        {label}
        <ChevronDown wh={16} color="#9CA3AF" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-[320px] rounded-xl border border-gray-100 bg-white p-3 shadow-pop">
          <div className="mb-2 flex items-center gap-1">
            <button
              type="button"
              aria-label={text('pickerPrevious')}
              onClick={() => step(-1)}
              className={arrow}
            >
              <ChevronLeft wh={18} color="#374151" />
            </button>
            <p className="flex-1 text-center text-sm font-semibold text-gray-900">
              {title}
            </p>
            <button
              type="button"
              aria-label={text('pickerNext')}
              disabled={nextOff}
              onClick={() => step(1)}
              className={arrow}
            >
              <ChevronRight wh={18} color="#374151" />
            </button>
          </div>
          {body}
        </div>
      )}
    </div>
  );
};

export default PeriodPicker;
